import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/common/PageHeader';
import { Search, BookOpen, Clock, Calendar, Users, Star, Filter, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { SEO, createBreadcrumbSchema } from '@/components/seo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCourses, useCourseCategories } from '@/hooks/use-course-api';
import { StaggerContainer, StaggerItem } from '@/components/common/AnimateOnScroll';
import EmptyState from '@/components/common/EmptyState';
import { CourseGridCardSkeleton } from '@/components/common/LoadingSkeleton';
import { motion } from 'framer-motion';
import { useCurrency } from '@/contexts/CurrencyContext';

interface CourseType {
  name: string;
  title: string;
  title_ar?: string;
  slug: string;
  category: string;
  level: string;
  short_description?: string;
  short_description_ar?: string;
  thumbnail?: string;
  price: number;
  currency: string;
  discount_price?: number;
  is_free: boolean;
  effective_price: number;
  instructor: string;
  instructor_name: string;
  total_lessons: number;
  total_duration_hours: number;
  enrolled_students: number;
  average_rating: number;
  total_reviews: number;
  featured: boolean;
}

const CourseCard = ({
  course,
  isRTL,
  t,
}: {
  course: CourseType;
  isRTL: boolean;
  t: (key: string) => string;
}) => {
  const { formatPrice } = useCurrency();
  const title = isRTL && course.title_ar ? course.title_ar : course.title;
  const description = isRTL && course.short_description_ar ? course.short_description_ar : course.short_description;

  return (
    <Link to={`/courses/${course.slug || course.name}`}>
      <motion.div
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
        className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-soft-lg hover:border-primary/20 transition-all duration-300 group h-full flex flex-col"
      >
        {/* Thumbnail */}
        <div className="aspect-video bg-muted overflow-hidden relative">
          <img
            src={course.thumbnail || ''}
            alt={title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          {!course.thumbnail && (
            <div className="w-full h-full flex items-center justify-center bg-primary/10">
              <BookOpen className="h-10 w-10 text-primary/40" />
            </div>
          )}
          {/* Level badge */}
          <span className="absolute top-3 start-3 px-2.5 py-1 bg-background/90 backdrop-blur-sm text-xs font-medium rounded-md text-foreground">
            {course.level}
          </span>
          {course.is_free && (
            <span className="absolute top-3 end-3 px-2.5 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-md">
              {t('teacherProfile.free')}
            </span>
          )}
        </div>

        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-semibold text-foreground text-lg mb-2 truncate-2">{title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed mb-3 truncate-2 flex-1">
            {description || ''}
          </p>

          {/* Meta */}
          <div className="flex flex-wrap gap-3 mb-4 text-xs text-muted-foreground">
            {course.total_duration_hours > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {course.total_duration_hours}h
              </span>
            )}
            {course.total_lessons > 0 && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {course.total_lessons} {t('teacherProfile.lessons')}
              </span>
            )}
            {course.enrolled_students > 0 && (
              <span className="flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {course.enrolled_students}
              </span>
            )}
          </div>

          {/* Rating */}
          {course.average_rating > 0 && (
            <div
              className="flex items-center gap-1.5 mb-4"
              aria-label={`${course.average_rating.toFixed(1)} ${t('courses.rating.outOf5Stars')}, ${course.total_reviews} ${t('courses.rating.reviews')}`}
            >
              <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" aria-hidden="true" />
              <span aria-hidden="true" className="text-sm font-medium text-foreground">{course.average_rating.toFixed(1)}</span>
              <span aria-hidden="true" className="text-xs text-muted-foreground">({course.total_reviews})</span>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-3 border-t border-border mt-auto">
            <div>
              {course.is_free ? (
                <span className="text-lg font-bold text-primary">{t('teacherProfile.free')}</span>
              ) : course.discount_price ? (
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-foreground">{formatPrice(course.discount_price)}</span>
                  <span className="text-sm text-muted-foreground line-through">{formatPrice(course.price)}</span>
                </div>
              ) : (
                <span className="text-lg font-bold text-foreground">{formatPrice(course.effective_price || course.price)}</span>
              )}
            </div>
            <span className="text-xs font-medium text-primary group-hover:underline flex items-center gap-1">
              {t('courses.card.viewCourse')}
              <ArrowRight className="h-3 w-3 rtl-mirror" />
            </span>
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

const CoursesPage = () => {
  const { language, isRTL, t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data: coursesData, isLoading, error } = useCourses({
    search: searchQuery || undefined,
    category: selectedCategory || undefined,
    level: selectedLevel || undefined,
    page,
    limit: 12,
  });

  const { data: categories } = useCourseCategories();
  const courses = coursesData?.courses || [];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      createBreadcrumbSchema(
        [
          { name: t('nav.home'), url: 'https://hudanlilmuttaqeen.com/' },
          { name: t('nav.courses'), url: 'https://hudanlilmuttaqeen.com/courses' }
        ],
        language === 'ar' ? 'ar' : 'en'
      ),
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "inLanguage": language === 'ar' ? 'ar' : 'en',
        "itemListElement": courses.map((c: CourseType, idx: number) => ({
          "@type": "ListItem",
          "position": idx + 1,
          "url": `https://hudanlilmuttaqeen.com/courses/${c.slug || c.name}`,
          "name": isRTL && c.title_ar ? c.title_ar : c.title
        }))
      }
    ]
  };

  return (
    <Layout>
      <SEO
        titleKey="seo.courses.title"
        descriptionKey="seo.courses.description"
        keywordsKey="seo.courses.keywords"
        structuredData={structuredData}
      />

      <PageHeader
        title={t('courses.page.title')}
        subtitle={t('courses.page.subtitle')}
        pattern="stars"
        size="sm"
      />

      <section className="section-sm bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="container">
          {/* Filters */}
          <div className="bg-card border border-border rounded-xl p-5 mb-8 shadow-soft">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-4">
              <Filter className="h-4 w-4" />
              {t('courses.filter.heading')}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className={`absolute ${isRTL ? 'end-3' : 'start-3'} top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4`} aria-hidden="true" />
                <Input
                  className={`${isRTL ? 'pe-10' : 'ps-10'} rounded-lg`}
                  placeholder={t('courses.filter.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  aria-label={t('courses.filter.searchAriaLabel')}
                  type="search"
                />
              </div>
              <Select value={selectedCategory || 'all'} onValueChange={(v) => { setSelectedCategory(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder={t('courses.category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('courses.filter.allCategories')}</SelectItem>
                  {categories?.map((cat) => (
                    <SelectItem key={cat.category} value={cat.category}>
                      {cat.category} ({cat.count})
                    </SelectItem>
                  ))}
                  {!categories?.length && (
                    <>
                      <SelectItem value="tajweed">{t('courses.categories.tajweed')}</SelectItem>
                      <SelectItem value="hifz">{t('courses.categories.hifz')}</SelectItem>
                      <SelectItem value="tafsir">{t('courses.categories.tafsir')}</SelectItem>
                      <SelectItem value="arabic">{t('courses.categories.arabic')}</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              <Select value={selectedLevel || 'all'} onValueChange={(v) => { setSelectedLevel(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="rounded-lg">
                  <SelectValue placeholder={t('courses.level')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('courses.filter.allLevels')}</SelectItem>
                  <SelectItem value="Beginner">{t('courses.levels.beginner')}</SelectItem>
                  <SelectItem value="Intermediate">{t('courses.levels.intermediate')}</SelectItem>
                  <SelectItem value="Advanced">{t('courses.levels.advanced')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <CourseGridCardSkeleton key={i} />
              ))}
            </div>
          ) : error ? (
            <EmptyState
              icon={BookOpen}
              title={t('courses.error.title')}
              description={t('courses.error.description')}
            />
          ) : courses.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title={t('courses.empty.title')}
              description={t('courses.empty.description')}
            />
          ) : (
            <>
              <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((course: CourseType) => (
                  <StaggerItem key={course.name}>
                    <CourseCard course={course} isRTL={isRTL} t={t} />
                  </StaggerItem>
                ))}
              </StaggerContainer>

              {coursesData && coursesData.pages > 1 && (
                <div className="mt-10 space-y-3 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      aria-label={isRTL ? 'الصفحة السابقة' : 'Previous page'}
                    >
                      {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                    <span className="text-sm text-muted-foreground px-3">
                      {page} / {coursesData.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(coursesData.pages, p + 1))}
                      disabled={page >= coursesData.pages}
                      aria-label={isRTL ? 'الصفحة التالية' : 'Next page'}
                    >
                      {isRTL ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {`${t('courses.pagination.showing')} ${courses.length} ${t('courses.pagination.of')} ${coursesData.total} ${t('courses.pagination.coursesNoun')}`}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default CoursesPage;
